/**
 * "AIS検査済" badge — shown on stock cards whose `inspectionStatus` is
 * `aiscore_done` (ADR 006).
 *
 * NOTE: this is a simplified, original UI mark (circular "AIS" + a check),
 * NOT a faithful reproduction of the AUCNET AIS logo, which is a registered
 * trademark of AUCNET Inc. If the official mark is ever required, swap the
 * inline SVG for an AUCNET-supplied asset — the public API of this component
 * (props + the "AIS検査済" label) stays the same.
 *
 * The gradient `id`s are uniquified with `useId()` so multiple badges on the
 * same page (a grid of cards) never collide on a shared `<defs>` id.
 */
import { useId } from 'react';

interface Props {
  /** Pixel size of the round mark. List ≈ 16-20, detail ≈ 56-72. */
  size?: number;
  className?: string;
  /** Accessible label + visible text. */
  title?: string;
  /** When true, render the "AIS検査済" text label next to the mark. */
  showLabel?: boolean;
}

export function AisCheckBadge({
  size = 18,
  className,
  title = 'AIS inspected',
  showLabel = false,
}: Props): JSX.Element {
  const gradientId = useId();

  const mark = (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      role="img"
      aria-label={title}
      focusable="false"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <title>{title}</title>
      <defs>
        <radialGradient id={gradientId} cx="50%" cy="38%" r="70%">
          <stop offset="0%" stopColor="#3d8fd6" />
          <stop offset="55%" stopColor="#1769c0" />
          <stop offset="100%" stopColor="#0a3f86" />
        </radialGradient>
      </defs>
      <circle cx="32" cy="32" r="31" fill={`url(#${gradientId})`} />
      <circle cx="32" cy="32" r="31" fill="none" stroke="#0a3f86" strokeWidth="1" />
      <text
        x="32"
        y="30"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontWeight="900"
        fontSize="22"
        fill="#ffffff"
      >
        AIS
      </text>
      {/* check mark below the wordmark */}
      <path
        d="M22 44 l7 7 l13 -15"
        fill="none"
        stroke="#ffd200"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  if (!showLabel) {
    return (
      <span className={className} style={{ display: 'inline-flex' }}>
        {mark}
      </span>
    );
  }

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem',
        fontSize: '0.78rem',
        fontWeight: 700,
        color: '#15803d',
      }}
    >
      {mark}
      {title}
    </span>
  );
}
